import { useRef, useState, useEffect, RefObject } from "react";
import ResizeObserver from "resize-observer-polyfill";

const initialState = { width: 0, height: 0 };

/**
 * A hook for having up to date dimensions. These dimensions
 * always include padding and edge size and include margin by
 * default. The included margin is divided by 2 because margins
 * are shared between neighboring elements. This gives us a more
 * accurate picture or how much space an element takes up.
 */
const useDimensions = (ref:RefObject<HTMLElement>,includeMargin=true) => {
  const [dimensions, setDimensions] = useState(initialState);
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    // Use ResizeObserver to know when something changed
    // but then use bounding box to get the full size
    // because the resize observer only gives us the
    // content size
    resizeObserverRef.current = new ResizeObserver((entries = []) => {
      entries.forEach((_) => {
        if (ref.current) {
          const width = ref.current.getBoundingClientRect().width
          const height = ref.current.getBoundingClientRect().height
  
          if (includeMargin) {
            const computedStyles = window.getComputedStyle(ref.current)
            const marginWidth = (pxStrToNum(computedStyles.marginLeft) + 
                                pxStrToNum(computedStyles.marginRight))/2
            const marginHeight = (pxStrToNum(computedStyles.marginTop) + 
                                 pxStrToNum(computedStyles.marginBottom))/2
            setDimensions({
              width:width+marginWidth,
              height:height+marginHeight,
            })
          } else {
            setDimensions({ width, height });
          }
        }

      });
    });
    // If the ref is there, set the observer
    if (ref.current) resizeObserverRef.current.observe(ref.current);
    // Make sure to unsubscribe when the element is gone to avoid mem leaks
    return () => {
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
    };
  }, [ref,includeMargin]);
  return dimensions;
};

const pxStrToNum = (pxStr:string) => {
  try {
    return Number(pxStr.replace("px",""))
  } catch {
    throw Error(`Expected pixel string. Got ${pxStr}`)
  }
}

export default useDimensions;